import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Camera, Upload, X, Loader2, RotateCcw, ShoppingCart, Download, FlipHorizontal, AlertTriangle, ShieldCheck, ExternalLink, ZoomIn } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/store/cartStore";
import { VariantQuickPicker } from "@/components/VariantQuickPicker";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface TryOnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productTitle: string;
  productImage: string;
  product?: ShopifyProduct;
}

function productNeedsVariantPicker(product: ShopifyProduct): boolean {
  const tags = product.tags || [];
  const hasEngraving = tags.some(t => t.toLowerCase().includes('חריטה') || t.toLowerCase().includes('engraving'));
  const hasIcon = tags.some(t => t.toLowerCase().includes('icon') || t.toLowerCase().includes('אייקון'));
  const variants = product.variants?.edges || [];
  const hasMultipleVariants = variants.length > 1 && !(variants.length === 1 && variants[0].node.title === 'Default Title');
  return hasEngraving || hasIcon || hasMultipleVariants;
}

// In-dialog live camera view
function CameraCapture({ onCapture, onCancel, t }: { onCapture: (dataUrl: string) => void; onCancel: () => void; t: (key: string, fallback: string) => string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  const startCamera = useCallback(async (mode: "user" | "environment") => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
      }
      setError(null);
    } catch {
      setError(t("cameraError", "לא ניתן לגשת למצלמה. אנא אפשר גישה בהגדרות הדפדפן."));
    }
  }, [stream, t]);

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      setStream(prev => { prev?.getTracks().forEach(t => t.stop()); return null; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const handleFlip = useCallback(async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    if (stream) stream.getTracks().forEach(t => t.stop());
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: newMode }, audio: false });
      setStream(s);
      setError(null);
    } catch {
      setError(t("switchCameraError", "לא ניתן להחליף מצלמה."));
    }
  }, [facingMode, stream, t]);

  const handleCapture = useCallback(() => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      if (facingMode === "user") {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(videoRef.current, 0, 0);
    }
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    stream?.getTracks().forEach(t => t.stop());
    onCapture(dataUrl);
  }, [facingMode, stream, onCapture]);

  const handleCancel = useCallback(() => {
    stream?.getTracks().forEach(t => t.stop());
    onCancel();
  }, [stream, onCancel]);

  return (
    <div className="space-y-3">
      {error ? (
        <div className="p-3 rounded-sm border border-destructive/30 bg-destructive/10 text-destructive text-xs text-center">
          {error}
        </div>
      ) : (
        <div className="relative aspect-[3/4] rounded-sm overflow-hidden border border-border bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
          />
          <button
            onClick={handleFlip}
            className="absolute top-2 left-2 rounded-full p-1.5 bg-background/70 hover:bg-background transition-colors"
            title={t("flipCamera", "החלף מצלמה")}
          >
            <FlipHorizontal className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleCancel} className="flex-1">
          {t("cancel", "ביטול")}
        </Button>
        {!error && (
          <Button size="sm" onClick={handleCapture} className="flex-1 gap-2">
            <Camera className="h-4 w-4" />
            {t("capture", "צלם")}
          </Button>
        )}
      </div>
    </div>
  );
}

export function TryOnDialog({ open, onOpenChange, productTitle, productImage, product }: TryOnDialogProps) {
  const isMobile = useIsMobile();
  const { t, isHebrew } = useStaticTranslation("tryOn");
  const [userImage, setUserImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  // showLightbox removed - zoom is now inline
  const addItem = useCartStore(state => state.addItem);

  const PROGRESS_STEPS = [
    { value: 15, text: t("step1", "מנתח את התמונה...") },
    { value: 35, text: t("step2", "מזהה את אזור הגוף...") },
    { value: 55, text: t("step3", "מלביש את התכשיט...") },
    { value: 75, text: t("step4", "מעבד פרטים...") },
    { value: 90, text: t("step5", "כמעט מוכן...") },
  ];

  const dir = isHebrew ? "rtl" : "ltr";

  const compressImage = useCallback(async (dataUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let width = img.width;
        let height = img.height;
        const maxDim = 800;
        if (width > height && width > maxDim) {
          height = (height * maxDim) / width;
          width = maxDim;
        } else if (height > maxDim) {
          width = (width * maxDim) / height;
          height = maxDim;
        }
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        let quality = 0.7;
        let base64 = canvas.toDataURL("image/jpeg", quality);
        while (base64.length > 500000 && quality > 0.2) {
          quality -= 0.1;
          base64 = canvas.toDataURL("image/jpeg", quality);
        }
        resolve(base64);
      };
      img.src = dataUrl;
    });
  }, []);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        const compressed = await compressImage(dataUrl);
        setUserImage(compressed);
        setErrorMessage(null);
      };
      reader.readAsDataURL(file);
    } catch {
      setErrorMessage(t("imageError", "שגיאה בעיבוד התמונה"));
    }
  }, [compressImage, t]);

  const handleCameraCapture = useCallback(async (dataUrl: string) => {
    setShowCamera(false);
    const compressed = await compressImage(dataUrl);
    setUserImage(compressed);
    setErrorMessage(null);
  }, [compressImage]);

  const openFilePicker = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleFile(file);
    };
    document.body.appendChild(input);
    input.click();
    setTimeout(() => document.body.removeChild(input), 1000);
  }, [handleFile]);

  const handleSubmit = useCallback(async () => {
    if (!userImage) return;

    setIsProcessing(true);
    setProgress(5);
    setProgressText(t("starting", "מתחיל..."));
    setErrorMessage(null);

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < PROGRESS_STEPS.length) {
        setProgress(PROGRESS_STEPS[stepIndex].value);
        setProgressText(PROGRESS_STEPS[stepIndex].text);
        stepIndex++;
      }
    }, 5000);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/virtual-tryon`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            image: userImage,
            productTitle,
            productImage,
          }),
        }
      );

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t("errorCreating", "שגיאה ביצירת התמונה"));
      }

      const data = await response.json();
      if (data.error) throw new Error(data.message || t("errorCreating", "שגיאה ביצירת התמונה"));

      setProgress(100);
      setProgressText(t("ready", "מוכן!"));
      await new Promise((r) => setTimeout(r, 400));
      setResultImage(data.resultImage);
    } catch (error) {
      console.error("Virtual try-on error:", error);
      setErrorMessage(error instanceof Error ? error.message : t("errorCreating", "שגיאה ביצירת התמונה"));
    } finally {
      setIsProcessing(false);
    }
  }, [userImage, productTitle, productImage, t, PROGRESS_STEPS]);

  const handleReset = () => {
    setUserImage(null);
    setResultImage(null);
    setProgress(0);
    setProgressText("");
    setErrorMessage(null);
    setShowCamera(false);
  };

  const handleClose = (value: boolean) => {
    if (!value) handleReset();
    onOpenChange(value);
  };

  const handleAddToCart = () => {
    if (!product) { handleClose(false); return; }
    if (productNeedsVariantPicker(product)) {
      setVariantPickerOpen(true);
    } else {
      const variant = product.variants?.edges?.[0]?.node;
      if (variant) {
        addItem({
          product,
          variantId: variant.id,
          variantTitle: variant.title,
          price: variant.price,
          quantity: 1,
          selectedOptions: variant.selectedOptions,
        });
      }
      handleClose(false);
    }
  };

  const dialogTitle = `${t("title", "נסה עליך —")} ${productTitle}`;

  const content = (
    <div className="space-y-4" dir={dir}>
      {resultImage ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground text-center">{t("before", "לפני")}</p>
              <div className="aspect-[3/4] rounded-sm overflow-hidden border border-border">
                <img src={userImage!} alt={t("before", "לפני")} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground text-center">{t("after", "אחרי")}</p>
              <div
                className="aspect-[3/4] rounded-sm overflow-hidden border border-primary/30 relative group"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  const img = e.currentTarget.querySelector('img');
                  if (img) {
                    img.style.transformOrigin = `${x}% ${y}%`;
                    img.style.transform = 'scale(2.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) {
                    img.style.transform = 'scale(1)';
                  }
                }}
                onTouchMove={(e) => {
                  const touch = e.touches[0];
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((touch.clientX - rect.left) / rect.width) * 100;
                  const y = ((touch.clientY - rect.top) / rect.height) * 100;
                  const img = e.currentTarget.querySelector('img');
                  if (img) {
                    img.style.transformOrigin = `${x}% ${y}%`;
                    img.style.transform = 'scale(2.2)';
                  }
                }}
                onTouchEnd={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) {
                    img.style.transform = 'scale(1)';
                  }
                }}
              >
                <img src={resultImage} alt={t("after", "אחרי")} className="w-full h-full object-cover transition-transform duration-200 ease-out" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-80 transition-opacity drop-shadow-md" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} className="gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" />
              {t("tryAgain", "נסה שוב")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                const a = document.createElement("a");
                a.href = resultImage!;
                a.download = `try-on-${productTitle.replace(/\s+/g, "-")}.jpg`;
                a.click();
              }}
            >
              <Download className="h-3.5 w-3.5" />
              {t("download", "הורדה")}
            </Button>
            {product ? (
              <Button size="sm" onClick={handleAddToCart} className="flex-1 gap-1.5">
                <ShoppingCart className="h-3.5 w-3.5" />
                {t("addToCart", "הוסף לעגלה")}
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => handleClose(false)} className="flex-1">
                {t("close", "סגור")}
              </Button>
            )}
          </div>
        </>
      ) : isProcessing ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm font-medium">{t("processing", "מעבד...")}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="aspect-[3/4] rounded-sm overflow-hidden border border-border relative">
              <img src={userImage!} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            </div>
            <div className="aspect-[3/4] rounded-sm overflow-hidden border border-border">
              <img src={productImage} alt={productTitle} className="w-full h-full object-contain bg-secondary/10 p-2" />
            </div>
          </div>
          <div className="space-y-2">
            <Progress value={progress} className="h-1.5" />
            <p className="text-xs text-center text-muted-foreground">{progressText}</p>
            <p className="text-[10px] text-center text-muted-foreground/60">{t("processTime", "התהליך עשוי לקחת עד דקה")}</p>
          </div>
        </div>
      ) : showCamera ? (
        <CameraCapture
          onCapture={handleCameraCapture}
          onCancel={() => setShowCamera(false)}
          t={t}
        />
      ) : (
        <>
          <div className="flex items-center gap-3 p-2 rounded-sm border border-border bg-muted/20">
            <img src={productImage} alt={productTitle} className="w-12 h-12 rounded-sm object-contain bg-background flex-shrink-0" />
            <p className="text-sm flex-1 min-w-0 line-clamp-2 leading-snug">{productTitle}</p>
          </div>

          {errorMessage && (
            <div className="p-2.5 rounded-sm border border-destructive/30 bg-destructive/10 text-destructive text-xs">
              {errorMessage}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {t("uploadPrompt", "העלה תמונה ברורה שלך — חזיתית, תאורה טובה, והאזור הרלוונטי (צוואר/אוזניים/ידיים) גלוי.")}
          </p>

          {userImage ? (
            <div className="space-y-3">
              <div className="relative aspect-[3/4] max-w-[180px] mx-auto rounded-sm overflow-hidden border border-border">
                <img src={userImage} alt={t("yourImage", "התמונה שלך")} className="w-full h-full object-cover" />
                <button
                  onClick={() => setUserImage(null)}
                  className="absolute top-1.5 right-1.5 rounded-sm p-1 bg-background/80 hover:bg-background transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <Button size="sm" onClick={handleSubmit} className="w-full gap-2">
                {t("tryOnMe", "הלבש עליי")}
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={openFilePicker}
                className="flex-1 gap-2 h-14 flex-col"
              >
                <Upload className="h-5 w-5" />
                <span className="text-xs">{t("upload", "העלאה")}</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCamera(true)}
                className="flex-1 gap-2 h-14 flex-col"
              >
                <Camera className="h-5 w-5" />
                <span className="text-xs">{t("capture", "צילום")}</span>
              </Button>
            </div>
          )}

          <div className="rounded-md border border-border/50 bg-muted/30 p-3 space-y-2 text-center">
            <div className="flex items-start gap-2 justify-center">
              <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-muted-foreground leading-relaxed text-start">
                {t("aiDisclaimer", "ההדמיה נעשית באמצעות בינה מלאכותית (AI). תיתכן סטייה מהתמונה המקורית של המוצר. התוצאה מיועדת להמחשה בלבד ואינה מחליפה את המוצר בפועל.")}
              </p>
            </div>
            <div className="border-t border-border/30 -mx-3" />
            <div className="flex items-start gap-2 justify-start">
              <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-muted-foreground leading-relaxed text-start">
                {t("imagesNotSaved", "התמונות לא נשמרות באתר שלנו ולא נאספות — השימוש חד-פעמי בלבד.")}
                {" "}
                <button
                  type="button"
                  onClick={() => setShowPrivacyPolicy(true)}
                  className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  {t("privacyPolicyLink", "מדיניות פרטיות AI")}
                  <ExternalLink className="h-2.5 w-2.5 inline" />
                </button>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {isMobile ? (
        <Sheet open={open} onOpenChange={handleClose}>
          <SheetContent side="bottom" className="rounded-t-2xl px-4 pt-4 pb-8">
            <SheetHeader className="mb-4">
              <SheetTitle className="text-sm font-medium leading-snug line-clamp-2">
                {dialogTitle}
              </SheetTitle>
            </SheetHeader>
            {content}
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent className="max-w-xl w-full p-6">
            <DialogHeader className="mb-3">
              <DialogTitle className="text-base font-medium leading-snug line-clamp-2">
                {dialogTitle}
              </DialogTitle>
            </DialogHeader>
            {content}
          </DialogContent>
        </Dialog>
      )}

      {product && variantPickerOpen && (
        <VariantQuickPicker
          product={product}
          open={variantPickerOpen}
          onClose={() => {
            setVariantPickerOpen(false);
            handleClose(false);
          }}
        />
      )}

      <Dialog open={showPrivacyPolicy} onOpenChange={setShowPrivacyPolicy}>
        <DialogContent className="max-w-md w-full p-6" dir={dir}>
          <DialogHeader className="mb-3">
            <DialogTitle className="text-base font-medium flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              {t("aiPrivacyTitle", "מדיניות פרטיות — הדמיית AI")}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t("aiPrivacyDesc", "מידע על עיבוד תמונות בשירות ההדמיה")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed" dir={dir}>
            <p>
              {t("aiPrivacy1", "שירות ההדמיה משתמש במודלים של בינה מלאכותית (AI) של חברות צד שלישי, כגון Google (Gemini) ו-OpenAI, לצורך עיבוד התמונה והדמיית התכשיט.")}
            </p>
            <p>
              {t("aiPrivacy2", "כחלק מתהליך ההדמיה, התמונה שהעלית נשלחת לשרתים חיצוניים של ספקי ה-AI לצורך עיבוד. ייתכן שתמונות אלו יישמרו באופן זמני במאגרים של ספקי השירות בהתאם למדיניות הפרטיות שלהם.")}
            </p>
            <p className="font-medium text-foreground">
              {t("aiPrivacy3", "באתר שלנו, התמונות שלך לא נשמרות כלל — לא בשרתים שלנו ולא במסד הנתונים שלנו. העיבוד מתבצע בזיכרון בלבד והתמונה נמחקת מיד לאחר סיום ההדמיה.")}
            </p>
            <p>
              {t("aiPrivacy4", "בשימוש בשירות זה, את/ה מסכים/ה לכך שהתמונה תועבר לספקי AI חיצוניים כמתואר לעיל.")}
            </p>
          </div>
          <div className="mt-2">
            <Button size="sm" onClick={() => setShowPrivacyPolicy(false)} className="w-full">
              {t("understood", "הבנתי")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox removed - zoom is now inline on hover/touch */}
    </>
  );
}
